import { useState } from "react";
import { DateTime } from "luxon";

// Types for availability
export enum AmenityDayAvailability {
  Unavailable = "Unavailable",
  Available = "Available",
  AvailableForCheckOut = "AvailableForCheckOut",
  AfterRange = "AfterRange",
}

export enum BookingAvailabilityType {
  ByDay = "ByDay",
  Range = "Range",
}

export enum CalendarTileStatus {
  Available = "Available",
  Unavailable = "Unavailable",
  OnlySelected = "OnlySelected",
  StartSelected = "StartSelected",
  MiddleSelected = "MiddleSelected",
  EndSelected = "EndSelected",
}

export enum BookingError {
  None,
  CompositionError,
  CheckOutOnlyError,
  LowerThanMinimum,
  AboveMaximum,
}

// Amenity day item
export interface AmenityDayItem {
  key: string;
  date: DateTime;
  availability: AmenityDayAvailability;
  isSelected: boolean;
  viewStatus: CalendarTileStatus;
}

// Booking Engine State
export interface AmenityBookingState {
  dayItems: Record<string, AmenityDayItem>;
  error: BookingError;
  selectedKeys: string[];
}

// Booking Engine Hook Options
export interface AmenityBookingOptions {
  initialDayItems: AmenityDayItem[];
  availabilityType: BookingAvailabilityType;
  minimumDuration?: number;
  maximumDuration?: number;
  onSelectionChange?: (selectedKeys: string[]) => void; // callback when selection updates
}

// Helper functions
const MINIMUM_DURATION_IN_NIGHTS = 1;
const UNLIMITED_DURATION = Number.MAX_SAFE_INTEGER;

function safeMinimumDuration(minimumDuration?: number) {
  return !minimumDuration || minimumDuration < MINIMUM_DURATION_IN_NIGHTS
    ? MINIMUM_DURATION_IN_NIGHTS
    : minimumDuration;
}

function validateCanSelectRange(
  lastSelectedDate: DateTime,
  dayItems: Record<string, AmenityDayItem>,
  limit: number
): boolean {
  for (let i = 1; i <= limit; i++) {
    const nextDate = lastSelectedDate.plus({ days: i });
    const key = nextDate.toISODate();
    if (!key) continue;
    const avail = dayItems[key]?.availability;
    if (
      !avail ||
      avail === AmenityDayAvailability.Unavailable ||
      avail === AmenityDayAvailability.AfterRange
    )
      return false;
    if (avail === AmenityDayAvailability.AvailableForCheckOut && i !== limit)
      return false;
  }
  return true;
}

function validateCanSelectMinimum(
  selectedItem: AmenityDayItem,
  dayItems: Record<string, AmenityDayItem>,
  minimumDuration?: number
): boolean {
  const lastSelectedDate = selectedItem.date;
  const plusDaysLimit = safeMinimumDuration(minimumDuration);
  return validateCanSelectRange(lastSelectedDate, dayItems, plusDaysLimit);
}

// Main Hook
export function useAmenityBookingEngine(options: AmenityBookingOptions) {
  const {
    initialDayItems,
    availabilityType,
    minimumDuration,
    maximumDuration,
    onSelectionChange,
  } = options;

  const selectionChangeCb =
    typeof onSelectionChange === "function" ? onSelectionChange : () => {};

  // Use ISODate as key
  const initMap: Record<string, AmenityDayItem> = {};
  // Determine the last available date key (highest chronological) for RANGE selection.
  const lastAvailableKey =
    availabilityType === BookingAvailabilityType.Range
      ? initialDayItems
          .filter((d) => d.availability === AmenityDayAvailability.Available)
          .sort((a, b) => a.date.toMillis() - b.date.toMillis())
          .slice(-1)[0]?.key
      : undefined;

  initialDayItems.forEach((d) => {
    // If this is the last available day and we are in Range mode, initially disable it
    // so it can't be chosen as the first (start) date.
    if (
      availabilityType === BookingAvailabilityType.Range &&
      lastAvailableKey &&
      d.key === lastAvailableKey
    ) {
      initMap[d.key] = {
        ...d,
        viewStatus: CalendarTileStatus.Unavailable,
      };
    } else {
      initMap[d.key] = d;
    }
  });

  const [dayItems, setDayItems] =
    useState<Record<string, AmenityDayItem>>(initMap);
  const [error, setError] = useState<BookingError>(BookingError.None);

  // Centralized updater to ensure callback fires after selection changes
  function updateItems(
    items: Record<string, AmenityDayItem>,
    newError?: BookingError
  ) {
    setDayItems(items);
    if (typeof newError !== "undefined") setError(newError);
    selectionChangeCb(getSelectedKeys(items));
  }

  function clearSelections(items: Record<string, AmenityDayItem>) {
    const out: Record<string, AmenityDayItem> = {};
    Object.values(items).forEach((it) => {
      out[it.key] = {
        ...it,
        isSelected: false,
        viewStatus: CalendarTileStatus.Available,
      };
    });
    // After clearing (no start date selected), re-disable the last available date for RANGE bookings.
    if (
      availabilityType === BookingAvailabilityType.Range &&
      lastAvailableKey &&
      out[lastAvailableKey]
    ) {
      out[lastAvailableKey] = {
        ...out[lastAvailableKey],
        viewStatus: CalendarTileStatus.Unavailable,
      };
    }
    return out;
  }

  function getSelectedKeys(items: Record<string, AmenityDayItem>) {
    return Object.values(items)
      .filter((d) => d.isSelected)
      .map((d) => d.key);
  }

  // Selection logic
  function selectDay(dayKey: string) {
    let newItems = { ...dayItems };
    const selectedItem = newItems[dayKey];
    if (!selectedItem) {
      updateItems(newItems, BookingError.CompositionError);
      return;
    }

    if (availabilityType === BookingAvailabilityType.ByDay) {
      newItems = clearSelections(newItems);
      if (selectedItem.availability === AmenityDayAvailability.Available) {
        newItems[dayKey] = {
          ...selectedItem,
          isSelected: true,
          viewStatus: CalendarTileStatus.OnlySelected,
        };
        updateItems(newItems, BookingError.None);
      }
      return;
    }

    // Range selection
    const selectedKeys = getSelectedKeys(newItems);

    // Prevent selecting the last available date as the first (start) date.
    if (
      availabilityType === BookingAvailabilityType.Range &&
      selectedKeys.length === 0 &&
      lastAvailableKey &&
      dayKey === lastAvailableKey
    ) {
      // This will naturally fail minimum duration validation, but we make it explicit.
      updateItems(newItems, BookingError.LowerThanMinimum);
      return;
    }

    if (selectedKeys.length === 0) {
      // First selection
      newItems = clearSelections(newItems);
      singleSelection(selectedItem, newItems);
    } else if (selectedKeys.length === 1) {
      extendSelection(selectedItem, newItems);
    } else {
      // More than 1 selected, reset
      newItems = clearSelections(newItems);
      singleSelection(selectedItem, newItems);
    }
    // singleSelection / extendSelection internally call updateItems; avoid duplicate here.
  }

  function singleSelection(
    selectedItem: AmenityDayItem,
    items: Record<string, AmenityDayItem>
  ) {
    if (
      selectedItem.availability === AmenityDayAvailability.AvailableForCheckOut
    ) {
      updateItems(items, BookingError.CheckOutOnlyError);
      return;
    }
    if (validateCanSelectMinimum(selectedItem, items, minimumDuration)) {
      const orderedKeys = Object.keys(items);
      const selectedIndex = orderedKeys.indexOf(selectedItem.key);
      let blockFollowingDays = false; // Once we hit an Unavailable after the selected start, everything after becomes unavailable.

      orderedKeys.forEach((key, i) => {
        const dayItem = items[key];
        const daysBetween = selectedItem.date.diff(dayItem.date, "days").days;
        const canExtendTo =
          validateCanExtendSelection(daysBetween, maximumDuration) &&
          validateExtensionIsContinuous(selectedItem.date, items, daysBetween);

        // Always set explicit viewStatus instead of early-return so downstream days can react to block flag.
        if (key === selectedItem.key) {
          items[key] = {
            ...selectedItem,
            isSelected: true,
            viewStatus: CalendarTileStatus.OnlySelected,
          };
          return;
        }

        // Days before the selected date are not selectable for range start extension.
        if (i < selectedIndex) {
          items[key] = {
            ...dayItem,
            viewStatus: CalendarTileStatus.Unavailable,
          };
          return;
        }

        // If we've already encountered a blocking (Unavailable) day after selection, everything after stays unavailable.
        if (blockFollowingDays) {
          items[key] = {
            ...dayItem,
            viewStatus: CalendarTileStatus.Unavailable,
          };
          return;
        }

        // If this day itself is Unavailable, mark it and activate blocking for subsequent days.
        if (dayItem.availability === AmenityDayAvailability.Unavailable) {
          blockFollowingDays = true;
          items[key] = {
            ...dayItem,
            viewStatus: CalendarTileStatus.Unavailable,
          };
          return;
        }

        // Respect maximum duration limits.
        if (i > selectedIndex + (maximumDuration ?? UNLIMITED_DURATION)) {
          items[key] = {
            ...dayItem,
            viewStatus: CalendarTileStatus.Unavailable,
          };
          return;
        }

        // If the continuous extension check fails, treat as blocked from this point forward.
        if (!canExtendTo) {
          blockFollowingDays = true;
          items[key] = {
            ...dayItem,
            viewStatus: CalendarTileStatus.Unavailable,
          };
          return;
        }

        // Special case: AvailableForCheckOut can be shown as a selectable (end) day.
        if (
          dayItem.availability === AmenityDayAvailability.AvailableForCheckOut
        ) {
          items[key] = { ...dayItem, viewStatus: CalendarTileStatus.Available };
          return;
        }

        // Otherwise it's a normal extendable day.
        items[key] = { ...dayItem, viewStatus: CalendarTileStatus.Available };
      });
      updateItems(items, BookingError.None);
    } else {
      // Keep the selected start day even if minimum duration can't be satisfied.
      items[selectedItem.key] = {
        ...selectedItem,
        isSelected: true,
        viewStatus: CalendarTileStatus.OnlySelected,
      };
      // Immediate user feedback for minimum duration violation.
      if (typeof window !== "undefined") {
        const min = safeMinimumDuration(minimumDuration);
        window.alert(
          `Minimum booking duration is ${min} day${min > 1 ? "s" : ""}`
        );
      }
      updateItems(items, BookingError.LowerThanMinimum);
    }
  }

  function extendSelection(
    selectedItem: AmenityDayItem,
    items: Record<string, AmenityDayItem>
  ) {
    const lastSelected = Object.values(items).find((it) => it.isSelected);
    if (!lastSelected) {
      updateItems(items, BookingError.CompositionError);
      return;
    }
    const lastDate = lastSelected.date;
    const selectedDate = selectedItem.date;
    if (selectedDate < lastDate) {
      // Reset and select new
      items = clearSelections(items);
      singleSelection(selectedItem, items);
      return;
    }
    // Forward extension
    extendSelectionRange(lastDate, selectedDate, selectedItem, items);
  }

  function extendSelectionRange(
    lastSelectedDate: DateTime,
    selectedDate: DateTime,
    selectedItem: AmenityDayItem,
    items: Record<string, AmenityDayItem>
  ) {
    const daysToSelectedDay = selectedDate.diff(lastSelectedDate, "days").days;
    if (!validateCanExtendSelection(daysToSelectedDay, maximumDuration)) {
      // Maximum duration exceeded – keep original start selection and flag error.
      updateItems(items, BookingError.AboveMaximum);
      return;
    }
    if (
      !validateExtensionIsMoreThanMinimum(daysToSelectedDay, minimumDuration)
    ) {
      // Minimum duration not met – keep original start selection and flag error.
      updateItems(items, BookingError.LowerThanMinimum);
      return;
    }
    if (
      !validateExtensionIsContinuous(lastSelectedDate, items, daysToSelectedDay)
    ) {
      items = clearSelections(items);
      singleSelection(selectedItem, items);
      return;
    }
    selectRange(lastSelectedDate, items, daysToSelectedDay);
    updateItems(items, BookingError.None);
  }

  function validateCanExtendSelection(
    daysToSelectedDay: number,
    maxDuration?: number
  ) {
    const safeMaxDuration =
      !maxDuration || maxDuration < MINIMUM_DURATION_IN_NIGHTS
        ? UNLIMITED_DURATION
        : maxDuration;
    return daysToSelectedDay <= safeMaxDuration;
  }

  function validateExtensionIsMoreThanMinimum(
    daysToSelectedDay: number,
    minimumDuration?: number
  ) {
    return daysToSelectedDay >= safeMinimumDuration(minimumDuration);
  }

  function validateExtensionIsContinuous(
    lastSelectedDate: DateTime,
    items: Record<string, AmenityDayItem>,
    daysToSelectedDay: number
  ) {
    return validateCanSelectRange(lastSelectedDate, items, daysToSelectedDay);
  }

  function selectRange(
    lastSelectedDate: DateTime,
    items: Record<string, AmenityDayItem>,
    daysToSelectedDay: number
  ) {
    const lastKey = lastSelectedDate.toISODate();
    if (lastKey && items[lastKey]) {
      items[lastKey] = {
        ...items[lastKey],
        viewStatus: CalendarTileStatus.StartSelected,
        isSelected: true,
      };
    }
    for (let i = 1; i <= daysToSelectedDay; i++) {
      const nextDate = lastSelectedDate.plus({ days: i });
      const key = nextDate.toISODate();
      if (!key) {
        setError(BookingError.CompositionError);
        return;
      }
      const viewStatus =
        i === daysToSelectedDay
          ? CalendarTileStatus.EndSelected
          : CalendarTileStatus.MiddleSelected;
      const updated = items[key]
        ? {
            ...items[key],
            isSelected: true,
            viewStatus,
          }
        : undefined;
      if (!updated) {
        updateItems(items, BookingError.CompositionError);
        return;
      }
      items[key] = updated;
    }
  }

  // Reset all selections
  function resetSelections() {
    const cleared = clearSelections(dayItems);
    updateItems(cleared, BookingError.None);
  }

  console.log("DAY ITEMS", dayItems);

  // API for UI
  return {
    dayItems,
    error,
    selectedKeys: getSelectedKeys(dayItems),
    selectDay,
    resetSelections,
  };
}
